import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Wallet, Repeat, ShoppingBag, ShieldCheck, MapPin, ShieldOff, ShieldQuestion, KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toFixed(2)}`;
const ROLES = ["customer", "pharmacist", "admin"];

const Content = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: me } = useAuth();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [resetting, setResetting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/users/${id}`);
            setUser(r.data);
        } catch {
            toast.error("User not found");
            navigate("/admin/users");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const changeRole = async (newRole) => {
        if (newRole === user.role) return;
        if (!window.confirm(`Change role to ${newRole}?`)) return;
        setBusy(true);
        try {
            await api.patch(`/admin/users/${id}/role`, { role: newRole });
            toast.success(`Role updated to ${newRole}`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Failed");
        } finally { setBusy(false); }
    };
    const toggleDisabled = async () => {
        if (id === me?.id) { toast.error("You cannot disable yourself."); return; }
        const action = user.disabled ? "enable" : "disable";
        if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this account?`)) return;
        setBusy(true);
        try {
            await api.post(`/admin/users/${id}/${action}`);
            toast.success(`Account ${action}d`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Failed");
        } finally { setBusy(false); }
    };

    const confirmReset2fa = async () => {
        if (resetting) return;
        setResetting(true);
        try {
            const { data } = await api.post(`/admin/users/${id}/reset-2fa`);
            toast.success(
                data?.was_enabled
                    ? `2FA reset for ${user.email} · ${data.sessions_revoked ?? 0} session(s) revoked`
                    : `2FA was already off for ${user.email} · action logged`,
            );
            setResetOpen(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Reset failed");
        } finally { setResetting(false); }
    };

    if (loading || !user) return <div className="h-[60vh] skeleton rounded-2xl" />;

    const isSelf = id === me?.id;

    return (
        <div className="space-y-5" data-testid="admin-user-detail">
            <Link to="/admin/users" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-700">
                <ArrowLeft className="h-4 w-4" /> All users
            </Link>

            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
                <div className="space-y-5">
                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <p className="label-uppercase">Profile</p>
                        <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{user.full_name}</h2>
                        <p className="text-sm text-slate-500">{user.email} · {user.phone}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-brand-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase text-brand-700" data-testid="user-role-pill">{user.role}</span>
                            {user.disabled && <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase text-rose-600" data-testid="user-disabled-pill">Disabled</span>}
                            <span className="text-xs text-slate-500">Joined {new Date(user.created_at).toLocaleDateString("en-IN")}</span>
                        </div>
                    </section>

                    <div className="grid gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-soft" data-testid="user-stat-wallet">
                            <Wallet className="h-4 w-4 text-brand-700" />
                            <p className="mt-2 text-xs text-slate-500">Wallet balance</p>
                            <p className="mt-1 text-xl font-semibold tabular-nums">{inr(user.wallet_balance)}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-soft" data-testid="user-stat-subs">
                            <Repeat className="h-4 w-4 text-brand-700" />
                            <p className="mt-2 text-xs text-slate-500">Active subscriptions</p>
                            <p className="mt-1 text-xl font-semibold tabular-nums">{user.active_subscriptions}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-soft" data-testid="user-stat-rx">
                            <ShieldQuestion className="h-4 w-4 text-amber-600" />
                            <p className="mt-2 text-xs text-slate-500">Pending prescriptions</p>
                            <p className="mt-1 text-xl font-semibold tabular-nums">{user.pending_prescriptions}</p>
                        </div>
                    </div>

                    {user.recent_orders?.length > 0 && (
                        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                            <p className="label-uppercase">Recent orders</p>
                            <ul className="mt-3 divide-y divide-slate-100">
                                {user.recent_orders.map((o) => (
                                    <li key={o.id} className="flex items-center gap-3 py-2">
                                        <Link to={`/admin/orders/${o.id}`} className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-900 hover:text-brand-700">{o.order_no}</p>
                                            <p className="text-xs text-slate-500">{o.status} · {new Date(o.created_at).toLocaleDateString("en-IN")}</p>
                                        </Link>
                                        <p className="text-sm font-semibold tabular-nums">{inr(o.amounts?.total)}</p>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {user.addresses?.length > 0 && (
                        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                            <p className="label-uppercase">Addresses ({user.addresses.length})</p>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                {user.addresses.map((a) => (
                                    <div key={a.id} className="rounded-lg border border-slate-100 p-3 text-xs">
                                        <p className="flex items-center gap-1.5 font-medium text-slate-900"><MapPin className="h-3 w-3" /> {a.label}{a.is_default && <span className="text-[9px] text-brand-700">DEFAULT</span>}</p>
                                        <p className="mt-1 text-slate-600">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                                        <p className="text-slate-600">{a.city}, {a.state} - {a.pincode}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                <aside className="space-y-5">
                    {!isSelf && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                            <p className="label-uppercase">Role</p>
                            <div className="mt-3 flex flex-col gap-2" data-testid="user-role-buttons">
                                {ROLES.map((r) => (
                                    <button
                                        key={r}
                                        onClick={() => changeRole(r)}
                                        disabled={busy || user.role === r}
                                        className={`rounded-lg border px-3 py-2 text-sm font-medium capitalize transition-colors ${
                                            user.role === r ? "border-brand-300 bg-brand-50 text-brand-700" : "border-slate-200 text-slate-700 hover:border-brand-300 hover:text-brand-700"
                                        } disabled:opacity-50`}
                                        data-testid={`user-role-${r}-btn`}
                                    >
                                        {user.role === r && "✓ "}{r}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <p className="label-uppercase">Account</p>
                        <button
                            disabled={busy || isSelf}
                            onClick={toggleDisabled}
                            className={`mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                                user.disabled ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-rose-200 text-rose-600 hover:bg-rose-50"
                            }`}
                            data-testid="user-disable-toggle-btn"
                        >
                            {user.disabled ? (<><ShieldCheck className="h-4 w-4" /> Re-enable account</>) : (<><ShieldOff className="h-4 w-4" /> Disable account</>)}
                        </button>
                        {isSelf && <p className="mt-2 text-[11px] text-slate-500">You cannot disable yourself.</p>}
                    </div>

                    {/* Phase 3 — 2FA reset card */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="admin-user-2fa-card">
                        <p className="label-uppercase">Two-factor authentication</p>
                        <div className="mt-3 flex items-center justify-between gap-3">
                            <span
                                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                                    user.twofa?.enabled
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-slate-100 text-slate-600"
                                }`}
                                data-testid="admin-user-2fa-status"
                            >
                                {user.twofa?.enabled ? <ShieldCheck className="h-3 w-3" /> : <ShieldOff className="h-3 w-3" />}
                                {user.twofa?.enabled ? "Enabled" : "Disabled"}
                            </span>
                            {user.twofa?.enabled_at && (
                                <span className="text-[10px] text-slate-400">
                                    Since {new Date(user.twofa.enabled_at).toLocaleDateString("en-IN")}
                                </span>
                            )}
                        </div>
                        {user.twofa?.enabled && !isSelf && (
                            <button
                                onClick={() => setResetOpen(true)}
                                disabled={busy || resetting}
                                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                                data-testid="admin-user-reset-2fa-btn"
                            >
                                <KeyRound className="h-4 w-4" /> Reset 2FA (unlock account)
                            </button>
                        )}
                        {isSelf && user.twofa?.enabled && (
                            <p className="mt-2 text-[11px] text-slate-500">You cannot reset your own 2FA — use your recovery codes or ask another admin.</p>
                        )}
                        {!user.twofa?.enabled && (
                            <p className="mt-3 text-[11px] text-slate-500">User has not enabled 2FA. Nothing to reset.</p>
                        )}
                    </div>
                </aside>
            </div>

            {/* Phase 3 — 2FA reset confirmation modal */}
            {resetOpen && (
                <div
                    className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4"
                    onClick={() => !resetting && setResetOpen(false)}
                    data-testid="admin-user-reset-2fa-modal"
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                                    <KeyRound className="h-5 w-5" />
                                </div>
                                <h3 className="mt-3 text-lg font-semibold text-slate-900">Reset 2FA for {user.email}?</h3>
                                <p className="mt-1 text-sm text-slate-600">
                                    This will disable their 2FA, wipe their TOTP secret + backup codes, and force them to log in again on every device.
                                    <span className="mt-2 block font-medium text-slate-800">This action is logged in the admin audit trail.</span>
                                </p>
                            </div>
                            <button
                                onClick={() => !resetting && setResetOpen(false)}
                                className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"
                                aria-label="Close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={() => setResetOpen(false)}
                                disabled={resetting}
                                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                data-testid="admin-user-reset-2fa-cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmReset2fa}
                                disabled={resetting}
                                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700 disabled:opacity-50"
                                data-testid="admin-user-reset-2fa-confirm-btn"
                            >
                                {resetting ? "Resetting…" : "Yes, reset 2FA"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminUserDetail = () => (
    <AdminAuthGuard requireAdmin>
        <AdminLayout title="User profile" subtitle="Profile, roles and account state">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminUserDetail;
